import { MembershipType } from '../../view/admin/ad-management/Memberships'
import { useCallback, useEffect, useState, useRef } from 'react'
import { API } from '../../api'
import { TADMembership } from '../../type'
import { LocalStorageManager } from '../../utility/local-storage-manager'
import { handleArrayBufferBackendError } from '../../utility/array-buffer-toast-handling'
import { TADMembershipTemplate } from '../../type/ad-management'

export const LAST_MEMBERSHIPS_TAB_ACCESSED = 'lastMembershipsTabAccessed'

type TUseMemberships = {
  isLoading: boolean
  memberships: TADMembership[] | TADMembershipTemplate[]
  search: string
  setSearch: (search: string) => void
  page: number
  setPage: (page: number) => void
  totalCount: number
  maxPageSize: number
  getMemberships: () => void
  setMemberships: (memberships: TADMembership[] | TADMembershipTemplate[]) => void
  handleExportMembershipButtonClick: (type: MembershipType) => void
}

const MEMBERSHIP_KEY_EXTERNAL = 'memberships_external'
const MEMBERSHIP_KEY_INTERNAL = 'memberships_internal'
const MEMBERSHIP_KEY_TEMPLATE = 'memberships_template'

export const useMemberships = (type: MembershipType): TUseMemberships => {
  const [isLoading, setIsLoading] = useState(true)
  const [externalMemberships, setExternalMemberships] = useState<TADMembership[]>([])
  const [internalMemberships, setInternalMemberships] = useState<TADMembership[]>([])
  const [templateMemberships, setTemplateMemberships] = useState<TADMembershipTemplate[]>([])
  const [search, setSearch] = useState('')
  const [externalPage, setExternalPage] = useState(1)
  const [internalPage, setInternalPage] = useState(1)
  const [templatePage, setTemplatePage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [maxPageSize, setMaxPageSize] = useState(0)

  const abortControllerRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)

  const getMemberships = useCallback(
    async (pageToFetch: number) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()
      setIsLoading(true)
      requestIdRef.current += 1
      const currentRequestId = requestIdRef.current
      try {
        let membershipTypeParam: '' | 'INT' | 'EXT' | 'TEMPLATE' | undefined = ''
        if (type === MembershipType.Internal) membershipTypeParam = 'INT'
        else if (type === MembershipType.Template) membershipTypeParam = 'TEMPLATE'
        else if (type === MembershipType.External) membershipTypeParam = 'EXT'
        const { max_page_size, page_number, total_count, results } = await API.ADManagement.membership.list(
          search,
          pageToFetch,
          membershipTypeParam,
          abortControllerRef.current.signal,
        )

        setMaxPageSize(Number(max_page_size))
        setTotalCount(Number(total_count))
        if (type === MembershipType.External) {
          setExternalMemberships(results as TADMembership[])
          setExternalPage(Number(page_number))
        } else if (type === MembershipType.Internal) {
          setInternalMemberships(results as TADMembership[])
          setInternalPage(Number(page_number))
        } else {
          setTemplateMemberships(results as TADMembershipTemplate[])
          setTemplatePage(Number(page_number))
        }
      } catch (error) {
        if ((error as { name?: string }).name === 'AbortError') {
          console.log('Request was aborted')
        } else {
          console.error('Request failed', error)
        }
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false)
        }
      }
    },
    [search, type],
  )

  useEffect(() => {
    setIsLoading(true)
    if (type === MembershipType.External) {
      getMemberships(externalPage)
    } else if (type === MembershipType.Internal) {
      getMemberships(internalPage)
    } else {
      getMemberships(templatePage)
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [getMemberships, externalPage, internalPage, templatePage, type])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (type === MembershipType.External) {
        setExternalPage(newPage)
      } else if (type === MembershipType.Internal) {
        setInternalPage(newPage)
      } else {
        setTemplatePage(newPage)
      }
    },
    [type],
  )

  const handleExportMembershipButtonClick = (type: MembershipType) => {
    // Only allow export for EXT and INT
    if (type === MembershipType.Template) return
    const isExternal = type === MembershipType.External
    try {
      void API.ADManagement.membership.download(isExternal, search)
    } catch (error) {
      handleArrayBufferBackendError(error)
    }
  }

  useEffect(
    () => () => {
      LocalStorageManager.removeItem(MEMBERSHIP_KEY_EXTERNAL)
      LocalStorageManager.removeItem(MEMBERSHIP_KEY_INTERNAL)
      LocalStorageManager.removeItem(MEMBERSHIP_KEY_TEMPLATE)
    },
    [],
  )

  return {
    isLoading,
    memberships:
      type === MembershipType.External ? externalMemberships : type === MembershipType.Internal ? internalMemberships : templateMemberships,
    search,
    setSearch,
    page: type === MembershipType.External ? externalPage : type === MembershipType.Internal ? internalPage : templatePage,
    setPage: handlePageChange,
    totalCount,
    maxPageSize,
    getMemberships: () =>
      type === MembershipType.External
        ? getMemberships(externalPage)
        : type === MembershipType.Internal
          ? getMemberships(internalPage)
          : getMemberships(templatePage),
    setMemberships: (memberships: TADMembership[] | TADMembershipTemplate[]) => {
      if (type === MembershipType.External) setExternalMemberships(memberships as TADMembership[])
      else if (type === MembershipType.Internal) setInternalMemberships(memberships as TADMembership[])
      else setTemplateMemberships(memberships as TADMembershipTemplate[])
    },
    handleExportMembershipButtonClick,
  }
}
